import React from 'react';
import { CharacterStats, StatType } from '../../../../../shared/CharacterTypes';
import type { ProgressionCostOfferWire } from '../../../../../shared/types/ProgressionCost';
import { TomeButton, TomeChapter } from '../../../primitives/tome';
import { getStatAbbreviation, getStatDescription } from './StatHelpers';
import { StatLedgerRow } from './StatLedgerRow';

interface PrimaryStatsPanelProps {
  stats: CharacterStats;
  statPoints: number;
  characterClass?: string;
  onStatIncrease?: (stat: StatType, amount?: number) => void;
  playerGold?: number;
  statRespecOffer?: ProgressionCostOfferWire | null;
  onRespecStats?: () => void;
}

const PRIMARY_STATS = [
  { stat: StatType.STRENGTH, label: 'Strength' },
  { stat: StatType.DEXTERITY, label: 'Dexterity' },
  { stat: StatType.INTELLIGENCE, label: 'Intelligence' },
  { stat: StatType.LUCK, label: 'Luck' },
] as const;

export const PrimaryStatsPanel: React.FC<PrimaryStatsPanelProps> = ({
  stats,
  statPoints,
  characterClass,
  onStatIncrease,
  playerGold = 0,
  statRespecOffer = null,
  onRespecStats,
}) => {
  const respecCost = statRespecOffer?.quote.cost ?? 0;
  const needsGold = statRespecOffer != null && playerGold < respecCost;

  return (
    <TomeChapter className="character-stat-section" title="Primary">
      <p className="character-stat-section__intro">
        Spendable attributes. Allocate points individually or in bulk.
      </p>
      <div className="character-stat-list" role="list">
        {PRIMARY_STATS.map(({ stat, label }) => (
          <StatLedgerRow
            key={stat}
            statType={stat}
            stats={stats}
            statPoints={statPoints}
            characterClass={characterClass}
            abbreviation={getStatAbbreviation(stat)}
            label={label}
            value={stats[stat]}
            description={getStatDescription(stat)}
            action={
              <div className="character-stat-action-group">
                <TomeButton
                  className="character-stat-action"
                  cut="clean"
                  disabled={statPoints <= 0 || !onStatIncrease}
                  aria-label={`Spend one point on ${label}`}
                  onClick={() => onStatIncrease?.(stat)}
                >
                  +1
                </TomeButton>
                {statPoints >= 5 && (
                  <TomeButton
                    className="character-stat-action"
                    cut="clean"
                    disabled={!onStatIncrease}
                    aria-label={`Spend 5 points on ${label}`}
                    onClick={() => onStatIncrease?.(stat, 5)}
                  >
                    +5
                  </TomeButton>
                )}
                {statPoints > 1 && (
                  <TomeButton
                    className="character-stat-action"
                    cut="clean"
                    disabled={!onStatIncrease}
                    aria-label={`Spend max (${statPoints}) points on ${label}`}
                    onClick={() => onStatIncrease?.(stat, statPoints)}
                  >
                    Max
                  </TomeButton>
                )}
              </div>
            }
          />
        ))}
      </div>
      {statRespecOffer && (
        <TomeButton
          className="character-stat-respec"
          tone="secondary"
          cut="clean"
          fullWidth
          disabled={!statRespecOffer.available || needsGold || !onRespecStats}
          onClick={onRespecStats}
          data-testid="stat-respec"
        >
          {needsGold ? `Need ${respecCost} Gold` : `Respec Stats — ${respecCost} Gold`}
        </TomeButton>
      )}
    </TomeChapter>
  );
};
